import React from "react";
import { 
  CalendarIcon, 
  ClockIcon, 
  UserIcon,
  ChatBubbleLeftEllipsisIcon,
  PaperClipIcon
} from "@heroicons/react/24/outline";
import { useTranslation } from "react-i18next";

export default function TaskCard({ task, onEdit }) {
  const { t } = useTranslation();

  const getPriorityCircleColor = (priority) => {
    switch (priority?.toLowerCase()) {
      case 'urgent':
        return 'bg-red-500';
      case 'high':
        return 'bg-orange-500';
      case 'medium':
        return 'bg-yellow-500';
      case 'low':
        return 'bg-green-500';
      default:
        return 'bg-gray-400';
    }
  };

  const getPriorityTextColor = (priority) => {
    switch (priority?.toLowerCase()) {
      case 'urgent':
        return 'text-red-700';
      case 'high':
        return 'text-orange-700';
      case 'medium':
        return 'text-yellow-700';
      case 'low':
        return 'text-green-700';
      default:
        return 'text-gray-600';
    }
  };

  const isOverdue = task.due_date && new Date(task.due_date) < new Date();
  const isDueSoon = task.due_date && 
    new Date(task.due_date) >= new Date() && 
    new Date(task.due_date) <= new Date(Date.now() + 7 * 24 * 60 * 60 * 1000);

  return (
    <div className="bg-white rounded-lg p-4 shadow-sm border border-gray-200 hover:shadow-md transition-shadow">
      {/* Header */}
      <div className="flex items-start justify-between mb-2">
        <h4 className="font-medium text-gray-900 text-sm leading-5 line-clamp-2 flex-1 pr-2">
          {task.title}
        </h4>
        <div className="flex items-center gap-1 flex-shrink-0">
          {task.category && (
            <div 
              className="flex items-center px-2 py-1 rounded-full"
              style={{ backgroundColor: task.category.color || '#6B7280' }}
            >
              <span className="text-xs font-medium text-white truncate max-w-20">
                {task.category.name}
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Description */}
      {task.description && (
        <p className="text-xs text-gray-600 mb-3 line-clamp-2">
          {task.description}
        </p>
      )}

      {/* Priority */}
      {task.priority && (
        <div className="flex items-center gap-2 mb-3">
          <div className={`w-3 h-3 rounded-full ${getPriorityCircleColor(task.priority)}`}></div>
          <span className={`text-xs font-medium capitalize ${getPriorityTextColor(task.priority)}`}>
            {task.priority}
          </span>
        </div>
      )}

      {/* Metadata */}
      <div className="space-y-2 mb-3">
        {/* Due Date */}
        {task.due_date && (
          <div className="flex items-center gap-1.5">
            <CalendarIcon className="w-3.5 h-3.5 text-gray-400" />
            <span className={`text-xs ${
              isOverdue ? 'text-red-600' : isDueSoon ? 'text-yellow-600' : 'text-gray-500'
            }`}>
              {new Date(task.due_date).toLocaleDateString()}
            </span>
          </div>
        )}

        {/* Event DateTime */}
        {task.event_datetime && (
          <div className="flex items-center gap-1.5">
            <ClockIcon className="w-3.5 h-3.5 text-gray-400" />
            <span className="text-xs text-gray-500">
              {new Date(task.event_datetime).toLocaleString()}
            </span>
          </div>
        )}


        {/* Time Tracking */}
        {(task.estimated_hours || task.actual_hours) && (
          <div className="flex items-center gap-1.5">
            <ClockIcon className="w-3.5 h-3.5 text-gray-400" />
            <span className="text-xs text-gray-500">
              {task.actual_hours || 0}h / {task.estimated_hours || 0}h
            </span>
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="flex items-center justify-between pt-2 border-t border-gray-100">
        {/* Assignee */}
        <div className="flex items-center gap-1.5">
          {task.assignee ? (
            <>
              <div className="w-6 h-6 bg-gray-200 rounded-full flex items-center justify-center">
                <UserIcon className="w-3.5 h-3.5 text-gray-500" />
              </div>
              <span className="text-xs text-gray-600 truncate max-w-20">
                {task.assignee.name}
              </span>
            </>
          ) : (
            <span className="text-xs text-gray-400">
              {t('Tasks.Unassigned')}
            </span>
          )}
        </div>

        {/* Indicators */}
        <div className="flex items-center gap-2">
          {task.comments_count > 0 && (
            <div className="flex items-center gap-1">
              <ChatBubbleLeftEllipsisIcon className="w-3.5 h-3.5 text-gray-400" />
              <span className="text-xs text-gray-500">{task.comments_count}</span>
            </div>
          )}
          
          {task.files_count > 0 && (
            <div className="flex items-center gap-1">
              <PaperClipIcon className="w-3.5 h-3.5 text-gray-400" />
              <span className="text-xs text-gray-500">{task.files_count}</span>
            </div>
          )}
        </div>
      </div>

      {/* Tags */}
      {task.tags && task.tags.length > 0 && (
        <div className="flex flex-wrap gap-1 mt-2">
          {task.tags.slice(0, 3).map(tag => (
            <span 
              key={tag.id}
              className="text-xs bg-iavisus-100 text-white px-2 py-0.5 rounded-full"
            >
              {tag.name}
            </span>
          ))}
          {task.tags.length > 3 && (
            <span className="text-xs text-gray-500">
              +{task.tags.length - 3}
            </span>
          )}
        </div>
      )}
    </div>
  );
}